import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";
import { Pencil, Plus, Trash2, RefreshCcw, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import { UserModal } from "@/components/users/UserModal";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { UserFilters, UserFilterState } from "@/components/users/UserFilters";
import { useTenant } from "@/contexts/TenantContext";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { getUserPhotoUrl, getUserInitials } from "@/utils/user";
import { logCrud } from "@/services/activities";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RaioXModal } from "@/components/radiox/RaioXModal";
import { formatPhone } from "@/lib/utils";
import UserDetails from "@/pages/UserDetails";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useChatwootToken } from "@/hooks/use-chatwoot-token";
import { getChatwootSDK } from "@/integrations/chatwoot/sdk";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface UserProfile {
  id: string;
  nome_completo: string;
  email: string;
  telefone: string | null;
  cargo: string | null;
  status: string;
  data_nascimento: string | null;
  tenant_id: string | null;
  role_id: string | null;
  chatwoot_contact_id?: number | null;
  foto_url?: string | null;
  chatwoot_avatar_url?: string | null;
  roles?: {
    name: string;
    slug: string;
  };
  tenants?: {
    name: string;
    slug: string;
  };
}

const Users = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string | null>(null);
  const [selectedTenantFilter, setSelectedTenantFilter] = useState<string>("");
  const [syncingUserId, setSyncingUserId] = useState<string | null>(null);
  const [filters, setFilters] = useState<UserFilterState>({
    searchTerm: "",
    status: [],
    roles: [],
    hasChatwoot: null,
    overdueOnly: null,
  });

  const queryClient = useQueryClient();
  const { tenantId, isAdminSaas } = useTenant();
  const { hasToken, markTokenInvalid } = useChatwootToken();
  const [userProfile, setUserProfile] = useState<{ id: string; name: string } | null>(null);

  const handleChatwootFailure = (
    error: unknown,
    fallbackMessage: string,
    tokenInvalidMessage: string
  ) => {
    const message = typeof (error as any)?.message === "string" ? (error as any).message : "";
    if (message.includes("401")) {
      markTokenInvalid();
      toast.error(tokenInvalidMessage);
    } else {
      toast.error(message || fallbackMessage);
    }
  };

  // Buscar usuários
  const { data: users, isLoading } = useQuery({
    queryKey: ["users", tenantId, selectedTenantFilter],
    queryFn: async () => {
      let query = supabase
        .from("profiles" as any)
        .select(`
          *,
          roles:role_id (name, slug),
          tenants:tenant_id (name, slug)
        `);

      // Admin SaaS vê todos, usuários normais veem apenas do próprio tenant
      if (!isAdminSaas && tenantId) {
        query = query.eq("tenant_id", tenantId);
      } else if (isAdminSaas && selectedTenantFilter) {
        query = query.eq("tenant_id", selectedTenantFilter);
      }

      const { data, error } = await query.order("nome_completo", { ascending: true }) as any;

      if (error) throw error;
      return (data || []) as UserProfile[];
    },
  });

  // Buscar tenants (para filtro do Admin SaaS)
  const { data: tenants = [] } = useQuery({
    queryKey: ["tenants-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("id, name, slug")
        .order("name", { ascending: true }) as any;

      if (error) throw error;
      return data || [];
    },
    enabled: isAdminSaas,
  });

  const { data: tenantsSubs = [] } = useQuery({
    queryKey: ["tenants-subscription-data"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("tenants" as any)
        .select("id, trial_ends_at, subscription_next_billing_date")
        .order("name", { ascending: true }) as any;
      if (error) throw error;
      return data || [];
    },
  });

  const { data: subTxs = [] } = useQuery({
    queryKey: ["all-subscription-transactions"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("subscription_transactions")
        .select("tenant_id, status, expiration_date, payment_date")
        .eq("status", "approved");
      if (error) throw error;
      return data || [];
    },
  });

  const overdueTenantIds = useMemo(() => {
    const set = new Set<string>();
    const now = new Date();
    (tenantsSubs || []).forEach((t: any) => {
      const txs = subTxs.filter((x: any) => x.tenant_id === t.id);
      const lifetime = txs.some((x: any) => x.expiration_date === null && x.status === "approved");
      if (lifetime) return;
      const hasActive = txs.some((x: any) => x.expiration_date && new Date(x.expiration_date) > now);
      if (hasActive) return;
      if (t.subscription_next_billing_date && new Date(t.subscription_next_billing_date) > now) return;
      if (t.trial_ends_at && new Date(t.trial_ends_at) > now) return;
      set.add(t.id);
    });
    return set;
  }, [tenantsSubs, subTxs]);

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Deletar usuário do auth (cascade vai deletar o profile)
      const { error } = await supabase.auth.admin.deleteUser(id);
      if (error) throw error;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Usuário excluído com sucesso!");
      if (userToDelete) {
        void logCrud({ entitySlug: "entity:profiles", operationSlug: "delete", entityId: userToDelete, screenSlug: "usuarios" });
      }
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
    onError: (error: any) => {
      toast.error("Erro ao excluir usuário: " + error.message);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
  });

  const handleEdit = (user: UserProfile) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    setUserToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (userToDelete) {
      deleteMutation.mutate(userToDelete);
    }
  };

  const handleNewUser = () => {
    setSelectedUser(null);
    setIsModalOpen(true);
  };

  // Abrir modal via query string e limpar parâmetro
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("newUser") === "1") {
      handleNewUser();
      params.delete("newUser");
      navigate({ pathname: location.pathname, search: params.toString() }, { replace: true });
    }
  }, [location.search]);

  const getStatusBadge = (status: string) => {
    const variants: Record<string, "default" | "secondary" | "destructive" | "success" | "outline"> = {
      ativo: "success",
      inativo: "secondary",
      suspenso: "destructive",
    };
    return variants[status] || "outline";
  };

  // Buscar roles para filtros
  const { data: rolesData = [] } = useQuery({
    queryKey: ["roles-list"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("roles" as any)
        .select("id, name, slug")
        .order("name", { ascending: true }) as any;

      if (error) throw error;
      return (data || [])
        .filter((r: any) => r.slug !== "admin_saas")
        .map((r: any) => ({ id: r.id, name: r.name })) as Array<{ id: string; name: string }>;
    },
  });

  const matchesFilters = (user: UserProfile) => {
    // Filtro por busca
    if (filters.searchTerm) {
      const searchLower = filters.searchTerm.toLowerCase();
      const matchesSearch =
        user.nome_completo?.toLowerCase().includes(searchLower) ||
        user.email?.toLowerCase().includes(searchLower);
      if (!matchesSearch) return false;
    }

    if (filters.overdueOnly) {
      if (!user.tenant_id || !overdueTenantIds.has(user.tenant_id)) return false;
    }

    // Filtro por status
    if (filters.status.length > 0) {
      if (!filters.status.includes(user.status)) return false;
    }

    // Filtro por role
    if (filters.roles.length > 0) {
      if (!user.role_id || !filters.roles.includes(user.role_id)) return false;
    }

    // Filtro por Chatwoot (apenas se houver token)
    if (hasToken && filters.hasChatwoot !== null) {
      const hasChatwoot = Boolean(user.chatwoot_contact_id);
      if (filters.hasChatwoot !== hasChatwoot) return false;
    }

    return true;
  };

  const filteredUsers = useMemo(() => {
    if (!users) return [] as UserProfile[];
    return users.filter((user) => matchesFilters(user));
  }, [users, filters, hasToken]);

  // Pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUsers.length / itemsPerPage);

  // Calcular contagens para os filtros
  const facetCounts = useMemo(() => {
    const counts = {
      status: {} as Record<string, number>,
      roles: {} as Record<string, number>,
      hasChatwoot: {
        vinculado: 0,
        naoVinculado: 0,
      },
      overdue: {
        vencido: 0,
      },
    };

    if (!users) return counts;

    users.forEach((user) => {
      const matchesOtherFilters = (exceptFilter?: "status" | "roles" | "hasChatwoot" | "overdue") => {
        if (filters.searchTerm) {
          const searchLower = filters.searchTerm.toLowerCase();
          const matchesSearch =
            user.nome_completo?.toLowerCase().includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower);
          if (!matchesSearch) return false;
        }

        if (exceptFilter !== "status" && filters.status.length > 0) {
          if (!filters.status.includes(user.status)) return false;
        }

        if (exceptFilter !== "roles" && filters.roles.length > 0) {
          if (!user.role_id || !filters.roles.includes(user.role_id)) return false;
        }

        if (hasToken && exceptFilter !== "hasChatwoot" && filters.hasChatwoot !== null) {
          const hasChatwoot = Boolean(user.chatwoot_contact_id);
          if (filters.hasChatwoot !== hasChatwoot) return false;
        }

        if (exceptFilter !== "overdue" && filters.overdueOnly) {
          if (!user.tenant_id || !overdueTenantIds.has(user.tenant_id)) return false;
        }

        return true;
      };

      // Contar status
      if (user.status && matchesOtherFilters("status")) {
        counts.status[user.status] = (counts.status[user.status] || 0) + 1;
      }

      // Contar roles
      if (user.role_id && matchesOtherFilters("roles")) {
        counts.roles[user.role_id] = (counts.roles[user.role_id] || 0) + 1;
      }

      // Contar Chatwoot (apenas se houver token)
      if (hasToken && matchesOtherFilters("hasChatwoot")) {
        if (user.chatwoot_contact_id) {
          counts.hasChatwoot.vinculado++;
        } else {
          counts.hasChatwoot.naoVinculado++;
        }
      }

      if (matchesOtherFilters("overdue")) {
        if (user.tenant_id && overdueTenantIds.has(user.tenant_id)) {
          counts.overdue.vencido++;
        }
      }
    });

    return counts;
  }, [users, filters, hasToken, overdueTenantIds]);

  const allStatuses = useMemo(() => {
    const statuses = new Set<string>();
    users?.forEach((u) => {
      if (u.status) statuses.add(u.status);
    });
    return Array.from(statuses).sort();
  }, [users]);

  const handleFiltersChange = (newFilters: UserFilterState) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const activeFilterChips = useMemo<{ id: string; label: React.ReactNode; onClear: () => void }[]>(() => {
    const chips: { id: string; label: React.ReactNode; onClear: () => void }[] = [];
    
    if (filters.searchTerm) {
      const term = filters.searchTerm;
      chips.push({
        id: `search:${term}`,
        label: `Busca: "${term}"`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, searchTerm: "" });
        },
      });
    }
    
    filters.status.forEach((s) => {
      chips.push({
        id: `status:${s}`,
        label: `Status: ${s}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, status: filters.status.filter((x) => x !== s) });
        },
      });
    });
    
    filters.roles.forEach((roleId) => {
      const role = rolesData.find((r) => r.id === roleId);
      chips.push({
        id: `role:${roleId}`,
        label: `Perfil: ${role?.name || roleId}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, roles: filters.roles.filter((x) => x !== roleId) });
        },
      });
    });
    
    if (hasToken && filters.hasChatwoot !== null) {
      chips.push({
        id: `chatwoot:${filters.hasChatwoot}`,
        label: `Chatwoot: ${filters.hasChatwoot ? "Vinculado" : "Não vinculado"}`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, hasChatwoot: null });
        },
      });
    }

    if (filters.overdueOnly) {
      chips.push({
        id: `assinatura:vencido`,
        label: `Assinatura: Plano vencido`,
        onClear: () => {
          setCurrentPage(1);
          setFilters({ ...filters, overdueOnly: null });
        },
      });
    }

    return chips;
  }, [filters, rolesData, hasToken]);

  return (
    <div className="space-y-6" data-cy="users-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight" data-cy="users-title">Usuários</h1>
          <p className="text-muted-foreground">
            Gerencie os usuários {isAdminSaas ? "do sistema" : "da sua empresa"}
          </p>
        </div>
        <Button onClick={handleNewUser} data-cy="new-user-btn">
          <Plus className="mr-2 h-4 w-4" />
          Novo Usuário
        </Button>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <CardTitle data-cy="users-list-title">Lista de Usuários</CardTitle>
                <CardDescription>
                  {filteredUsers.length || 0} {filteredUsers.length === 1 ? "usuário" : "usuários"}
                </CardDescription>
              </div>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                {isAdminSaas && (
                  <Select value={selectedTenantFilter} onValueChange={(v) => setSelectedTenantFilter(v === "all" ? "" : v)} data-cy="users-tenant-filter">
                    <SelectTrigger className="w-full sm:w-[200px]">
                      <SelectValue placeholder="Filtrar por tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos os tenants</SelectItem>
                      {tenants.map((tenant: any) => (
                        <SelectItem key={tenant.id} value={tenant.id}>
                          {tenant.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <div className="relative w-full sm:w-[200px]">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar..."
                    value={filters.searchTerm}
                    onChange={(e) => handleFiltersChange({ ...filters, searchTerm: e.target.value })}
                    className="pl-8"
                    data-cy="users-search-input"
                  />
                </div>
                <UserFilters
                  filters={filters}
                  onFiltersChange={handleFiltersChange}
                  statuses={allStatuses}
                  roles={rolesData}
                  counts={facetCounts}
                  hasChatwootToken={hasToken}
                  data-cy="users-filters"
                />
              </div>
            </div>
            {activeFilterChips.length > 0 && (
              <div className="text-[13px] md:text-sm text-muted-foreground">
                <span className="mr-2 font-bold">Filtros ativos:</span>
                <div className="inline-flex flex-wrap gap-0.5">
                  {activeFilterChips.map((chip) => (
                    <Badge key={chip.id} variant="secondary" className="h-5 px-1.5 py-0 inline-flex items-center gap-1 border border-gray-300">
                      <span>{chip.label}</span>
                      <button
                        type="button"
                        onClick={chip.onClear}
                        className="text-red-600 hover:bg-red-600 hover:text-white rounded-full w-4 h-4 inline-flex items-center justify-center leading-none"
                        aria-label={`Remover ${chip.label}`}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8" data-cy="users-loading">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="border rounded-md">
                <Table data-cy="users-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nome / Perfil / Status</TableHead>
                      <TableHead>Email</TableHead>
                      {isAdminSaas && <TableHead>Tenant</TableHead>}
                      <TableHead>Cargo</TableHead>
                      {hasToken && <TableHead>Chatwoot</TableHead>}
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody data-cy="users-table-body">
                    {paginatedUsers.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={isAdminSaas ? (hasToken ? 6 : 5) : (hasToken ? 5 : 4)}
                          className="text-center text-muted-foreground"
                          data-cy="users-empty-state"
                        >
                          {users && users.length > 0
                            ? "Nenhum usuário encontrado com os filtros aplicados."
                            : "Nenhum usuário encontrado"}
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10">
                                <AvatarImage src={getUserPhotoUrl(user) || undefined} alt={user.nome_completo} />
                                <AvatarFallback>
                                  {getUserInitials(user.nome_completo)}
                                </AvatarFallback>
                              </Avatar>
                              <div className="flex flex-col gap-2">
                                <span className="font-medium">{user.nome_completo}</span>
                                <div className="flex flex-wrap gap-2">
                                  {user.roles ? (
                                    <Badge variant="outline">
                                      {user.roles.name}
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-muted-foreground">
                                      Sem perfil
                                    </Badge>
                                  )}
                                  <Badge variant={getStatusBadge(user.status)}>
                                    {user.status}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </TableCell>
                          <TableCell>{user.email}</TableCell>
                          {isAdminSaas && (
                            <TableCell>
                              {user.tenants ? (
                                <Badge variant="secondary">
                                  {user.tenants.name}
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground text-sm">-</span>
                              )}
                            </TableCell>
                          )}
                          <TableCell>{user.cargo || "-"}</TableCell>
                          {hasToken && (
                            <TableCell>
                              {user.chatwoot_contact_id ? (
                                <span className="inline-flex items-center gap-1 text-xs rounded bg-emerald-100 text-emerald-700 px-2 py-1">
                                  Vinculado
                                </span>
                              ) : (
                                (() => {
                                  const hasSyncData = Boolean(user.telefone || user.email);
                                  if (!hasSyncData) {
                                    return (
                                      <TooltipProvider>
                                        <Tooltip>
                                          <TooltipTrigger asChild>
                                            <button
                                              type="button"
                                              className="inline-flex items-center gap-1 text-xs rounded bg-muted text-muted-foreground px-2 py-1 cursor-not-allowed"
                                              onClick={() => {
                                                toast.message("Para sincronizar este contato, adicione telefone e/ou email.");
                                              }}
                                            >
                                              <RefreshCcw className="h-3 w-3" />
                                              Pendente
                                            </button>
                                          </TooltipTrigger>
                                          <TooltipContent>
                                            Para sincronizar este contato, adicione telefone e/ou email.
                                          </TooltipContent>
                                        </Tooltip>
                                      </TooltipProvider>
                                    );
                                  }

                                  return (
                                    <button
                                      type="button"
                                      className="inline-flex items-center gap-1 text-xs rounded bg-amber-100 text-amber-700 px-2 py-1 hover:bg-amber-200 transition disabled:opacity-60 disabled:cursor-not-allowed"
                                      disabled={syncingUserId === user.id}
                                      onClick={async () => {
                                        if (syncingUserId) return;
                                        try {
                                          setSyncingUserId(user.id);
                                          const sdk = getChatwootSDK();
                                          await sdk.syncContact("profile", user.id);
                                          toast.success("Sincronização concluída.");
                                          queryClient.invalidateQueries({ queryKey: ["users"] });
                                        } catch (error: any) {
                                          handleChatwootFailure(
                                            error,
                                            "Falha ao sincronizar com o Chatwoot.",
                                            "Falha ao sincronizar com o Chatwoot. Token do Chatwoot inválido. Atualize o token em Configurações > Integrações > Chatwoot."
                                          );
                                        } finally {
                                          setSyncingUserId(null);
                                        }
                                      }}
                                    >
                                      <RefreshCcw className={`h-3 w-3 ${syncingUserId === user.id ? "animate-spin" : ""}`} />
                                      Pendente
                                    </button>
                                  );
                                })()
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="icon-chip-blue"
                                      onClick={() => setUserProfile({ id: user.id, name: user.nome_completo })}
                                    >
                                      <FileText className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-center">
                                      <p>Ver ficha completa e relatórios do usuário</p>
                                      <p className="font-bold">{user.nome_completo}</p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="icon-chip-green"
                                      onClick={() => handleEdit(user)}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-center">
                                      <p>Editar dados do usuário</p>
                                      <p className="font-bold">{user.nome_completo}</p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="icon-chip-red"
                                      onClick={() => handleDelete(user.id)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <div className="text-center">
                                      <p>Excluir usuário</p>
                                      <p className="font-bold">{user.nome_completo}</p>
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <TablePagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredUsers.length}
                  onPageChange={setCurrentPage}
                  onItemsPerPageChange={(value) => {
                    setItemsPerPage(value);
                    setCurrentPage(1);
                  }}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>

      <UserModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedUser(null);
        }}
        user={selectedUser}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent data-cy="user-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita
              e todos os dados do usuário serão permanentemente removidos.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-cy="user-delete-cancel">Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-cy="user-delete-confirm">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {userProfile && (() => {
        const u = (users || []).find((x: any) => x.id === userProfile.id);
        const photo = getUserPhotoUrl(u);
        const infoItems = [
          (u as any)?.created_at ? { value: `Usuário desde ${new Intl.DateTimeFormat('pt-BR').format(new Date((u as any).created_at))}` } : null,
          u?.email ? { value: u.email } : null,
          u?.telefone ? { value: formatPhone(u.telefone as string) } : null,
        ].filter(Boolean) as any;
        return (
          <RaioXModal
            open={!!userProfile}
            onOpenChange={() => setUserProfile(null)}
            imageUrl={photo || null}
            entityTypeLabel={"USUÁRIO"}
            entityName={userProfile.name}
            infoItems={infoItems}
            renderContent={({ periodRange, setLoading }) => (
              <UserDetails id={userProfile.id} embedded periodRange={periodRange} setLoading={setLoading} />
            )}
          />
        );
      })()}
    </div>
  );
};

export default Users;
